/**
 * V2 → V1 Product Data Adapter
 *
 * Transforms V2 API product data into V1 shapes expected by
 * ProductCard, ProductDetailSelectProvider, and related components.
 */

/**
 * Full V2→V1 product transformation for single product page.
 * Maps options→variants, variants→details, and sets defaults
 * for fields that don't exist in V2 (reviews, discounts).
 */
export function adaptV2ProductToV1(v2Product) {
  if (!v2Product) {
    return v2Product;
  }

  const options = v2Product.options || [];
  const v2Variants = v2Product.variants || [];

  // Build V1 "variants" from V2 "options"
  const v1Variants = options.map((opt, idx) => ({
    id: idx + 1,
    isSize: false,
    isColor: false,
    formField: {
      id: idx + 1,
      label: opt.name,
      values: opt.values.map((val) => ({
        key: val.key,
        values: val.name,
      })),
    },
    values: opt.values.map((val) => ({
      id: val.key,
      value: val.key,
    })),
  }));

  const productImages = v2Product.images || [];

  // Build V1 "details" from V2 "variants"
  const v1Details = v2Variants.map((variant) => {
    const optionValues = variant.optionValues || {};

    // Map option values in the same order as the options array to ensure
    // productVariantValue1Id..4Id align with v1Variants[0..3].id.
    // Normalize mismatched keys: if optionValues has "7_5" but option value key is "7-5",
    // resolve to the canonical option value key so variant selection matches correctly.
    const orderedValues = options.map((opt) => {
      const valKey = optionValues[opt.key];
      if (!valKey) {
        return null;
      }

      // Exact match — fast path
      if (opt.values.some((v) => v.key === valKey)) {
        return valKey;
      }

      // Fuzzy match: strip non-alphanumeric and compare
      const stripped = valKey.replace(/[^a-z0-9]/g, '');
      const fuzzy = opt.values.find((v) => v.key.replace(/[^a-z0-9]/g, '') === stripped);
      return fuzzy ? fuzzy.key : valKey;
    });

    // Resolve variant images to product images (variant images only have productImageId)
    const resolvedImages =
      variant.images?.length > 0
        ? variant.images
            .map((vi) => productImages.find((pi) => pi.id === vi.productImageId))
            .filter(Boolean)
        : [];
    const detailImages = resolvedImages.length > 0 ? resolvedImages : productImages;

    return {
      id: variant.id,
      title: variant.title || v2Product.title,
      quantity: variant.inventoryQty || 0,
      price: variant.price || 0,
      discountPercentage: 0,
      sku: variant.sku,
      weight: variant.weight,
      weightUnit: variant.weightUnit,
      images: detailImages,
      productVariantValue1Id: orderedValues[0] || null,
      productVariantValue2Id: orderedValues[1] || null,
      productVariantValue3Id: orderedValues[2] || null,
      productVariantValue4Id: orderedValues[3] || null,
      metafields: variant.metafields || [],
      values: options
        .map((opt, idx) => {
          const valKey = orderedValues[idx];
          if (!valKey) {
            return null;
          }

          return {
            id: `${opt.key}-${valKey}`,
            field: {
              id: idx + 1,
              label: opt.name,
              valueType: opt.type || 'default',
              values: opt.values.map((v) => ({
                key: v.key,
                values: v.name,
              })),
            },
            value: valKey,
            productVariantValueId: valKey,
          };
        })
        .filter(Boolean),
    };
  });

  return {
    ...v2Product,
    category: { id: v2Product.categoryId },
    isSingle: v2Variants.length <= 1,
    reviewCount: 0,
    reviewAverage: 0,
    details: v1Details,
    variants: v1Variants,
  };
}

/**
 * Adapt a V2 favorite response into { product, productDetail } for ProductCard.
 * V2 favorites have a flat shape: { product, productVariant, productImages, productVariantImages }.
 * This reassembles them into the nested V2 product shape and delegates to adaptV2ProductToCardProps.
 */
export function adaptV2FavoriteToCardProps(favorite) {
  if (!favorite) {
    return { product: null, productDetail: null };
  }

  const v2Product = {
    ...favorite.product,
    images: favorite.productImages || [],
    variants: [
      {
        ...(favorite.productVariant || {}),
        images: favorite.productVariantImages || [],
      },
    ],
  };

  return adaptV2ProductToCardProps(v2Product);
}

/**
 * Adapt a V2 enriched cart item to V1 cart item shape.
 * V2 cart item: { id, productId, productVariantId, quantity, product, productVariant, productImages, productVariantImages }
 * V1 cart item: { id, productId, productDetailId, quantity, product, productDetail }
 */
export function adaptV2CartItemToV1(cartItem) {
  if (!cartItem) {
    return cartItem;
  }

  const v2Product = {
    ...cartItem.product,
    images: cartItem.productImages || [],
    variants: [
      {
        ...(cartItem.productVariant || {}),
        images: cartItem.productVariantImages || [],
      },
    ],
  };

  const adapted = adaptV2ProductToV1(v2Product);
  const productDetail = adapted.details?.[0] || null;

  return {
    id: cartItem.id,
    productId: cartItem.productId,
    productDetailId: cartItem.productVariantId,
    productVariantId: cartItem.productVariantId,
    quantity: cartItem.quantity,
    product: adapted,
    productDetail: productDetail ? { ...productDetail, product: adapted } : null,
  };
}

/**
 * Lightweight V2→V1 transform for product cards in lists.
 * Returns { product, productDetail } shaped for ProductCard component.
 */
export function adaptV2ProductToCardProps(v2Product) {
  if (!v2Product) {
    return { product: null, productDetail: null };
  }

  const adapted = adaptV2ProductToV1(v2Product);
  const firstDetail = adapted.details?.[0] || null;

  return {
    product: adapted,
    productDetail: firstDetail
      ? { ...firstDetail, product: adapted }
      : { id: adapted.id, product: adapted, price: 0, quantity: 0 },
  };
}
